import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import {
  ScheduleAssignment,
  ScheduleTeam,
  ScheduleUser,
  ShiftTemplate,
  addDays,
  endOfMonth,
  endOfWeek,
  formatDateHuman,
  formatDateISO,
  MONTHS_PL,
  startOfMonth,
  startOfWeek,
} from './utils'
import ScheduleWeekView from './ScheduleWeekView'
import ScheduleMonthView from './ScheduleMonthView'
import ScheduleUserMatrixView from './ScheduleUserMatrixView'
import ShiftTemplatesModal from './ShiftTemplatesModal'
import AssignmentEditorModal from './AssignmentEditorModal'
import BulkAssignModal from './BulkAssignModal'
import TeamsModal from './TeamsModal'


type ViewMode = 'week' | 'month' | 'matrix'

interface Props {
  token: string
}

interface ContextResponse {
  client_id: number | null
  client_name: string | null
}

interface CurrentUser {
  id: number
  role: string
  full_name?: string | null
  username: string
}

export default function SchedulePage({ token }: Props) {
  const { showError, showSuccess } = useNotificationContext()
  const [me, setMe] = useState<CurrentUser | null>(null)
  const [clientId, setClientId] = useState<number | null>(null)
  const [clientName, setClientName] = useState<string | null>(null)
  const [view, setView] = useState<ViewMode>('week')
  const [anchor, setAnchor] = useState<Date>(new Date())
  const [onlyMine, setOnlyMine] = useState(false)

  const [templates, setTemplates] = useState<ShiftTemplate[]>([])
  const [users, setUsers] = useState<ScheduleUser[]>([])
  const [teams, setTeams] = useState<ScheduleTeam[]>([])
  const [assignments, setAssignments] = useState<ScheduleAssignment[]>([])
  const [loading, setLoading] = useState(false)

  const [templatesModalOpen, setTemplatesModalOpen] = useState(false)
  const [teamsModalOpen, setTeamsModalOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)

  const [editorState, setEditorState] = useState<{
    open: boolean
    assignment?: ScheduleAssignment
    defaultDate?: string
    defaultUserId?: number
  }>({ open: false })

  const canManage = useMemo(() => {
    if (!me) return false
    return ['KIEROWNIK', 'ADMIN', 'MASTER_ADMIN'].includes(me.role)
  }, [me])

  // Zakres dat do pobrania w zależności od widoku
  const range = useMemo(() => {
    if (view === 'week') {
      const from = startOfWeek(anchor)
      const to = endOfWeek(anchor)
      return { from, to }
    }
    if (view === 'month') {
      // Pobierz cały miesiąc + tygodnie graniczne (siatka miesiąca)
      const from = startOfWeek(startOfMonth(anchor))
      const to = endOfWeek(endOfMonth(anchor))
      return { from, to }
    }
    // matrix – cały miesiąc
    return { from: startOfMonth(anchor), to: endOfMonth(anchor) }
  }, [view, anchor])

  const fetchMe = useCallback(async () => {
    try {
      const r = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      if (r.ok) setMe(await r.json())
    } catch (e) {
      console.error(e)
    }
  }, [token])

  const fetchContext = useCallback(async () => {
    try {
      const r = await fetch('/api/users/me/context', { headers: { Authorization: `Bearer ${token}` } })
      if (r.ok) {
        const data: ContextResponse = await r.json()
        setClientId(data.client_id)
        setClientName(data.client_name)
      }
    } catch (e) {
      console.error(e)
    }
  }, [token])

  const fetchTemplates = useCallback(async () => {
    if (!clientId) {
      setTemplates([])
      return
    }
    try {
      const r = await fetch(`/api/schedule/shift-templates?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok) setTemplates(await r.json())
      else {
        const t = await r.text()
        console.error('Templates error:', t)
      }
    } catch (e) {
      console.error(e)
    }
  }, [clientId, token])

  const fetchUsers = useCallback(async () => {
    if (!clientId) {
      setUsers([])
      return
    }
    try {
      const r = await fetch(`/api/schedule/users?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok) setUsers(await r.json())
    } catch (e) {
      console.error(e)
    }
  }, [clientId, token])

  const fetchTeams = useCallback(async () => {
    if (!clientId) {
      setTeams([])
      return
    }
    try {
      const r = await fetch(`/api/schedule/teams?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok) setTeams(await r.json())
    } catch (e) {
      console.error(e)
    }
  }, [clientId, token])


  const fetchAssignments = useCallback(async () => {
    if (!clientId) {
      setAssignments([])
      return
    }
    setLoading(true)
    try {
      const params = new URLSearchParams({
        client_id: String(clientId),
        date_from: formatDateISO(range.from),
        date_to: formatDateISO(range.to),
      })
      if (onlyMine && me) params.set('user_id', String(me.id))
      const r = await fetch(`/api/schedule/assignments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok) setAssignments(await r.json())
      else {
        const t = await r.text()
        showError(`Nie udało się załadować grafiku: ${t}`)
      }
    } catch (e: any) {
      showError(`Błąd: ${e?.message || e}`)
    } finally {
      setLoading(false)
    }
  }, [clientId, range.from, range.to, onlyMine, me, token, showError])

  useEffect(() => { fetchMe() }, [fetchMe])
  useEffect(() => { fetchContext() }, [fetchContext])
  useEffect(() => { fetchTemplates(); fetchUsers(); fetchTeams() }, [fetchTemplates, fetchUsers, fetchTeams])

  useEffect(() => { fetchAssignments() }, [fetchAssignments])

  // Nawigacja
  const goPrev = () => {
    if (view === 'week') setAnchor(addDays(anchor, -7))
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))
  }
  const goNext = () => {
    if (view === 'week') setAnchor(addDays(anchor, 7))
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))
  }
  const goToday = () => setAnchor(new Date())

  const rangeLabel = useMemo(() => {
    if (view === 'week') {
      const from = startOfWeek(anchor)
      const to = endOfWeek(anchor)
      return `${formatDateHuman(from)} – ${formatDateHuman(to)}`
    }
    return `${MONTHS_PL[anchor.getMonth()]} ${anchor.getFullYear()}`
  }, [view, anchor])

  const handleDeleteAssignment = useCallback(async (id: number) => {
    if (!window.confirm('Usunąć ten wpis grafiku?')) return
    try {
      const r = await fetch(`/api/schedule/assignments/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok || r.status === 204) {
        showSuccess('Wpis usunięty')
        fetchAssignments()
      } else {
        const t = await r.text()
        showError(`Nie udało się usunąć: ${t}`)
      }
    } catch (e: any) {
      showError(`Błąd: ${e?.message || e}`)
    }
  }, [token, fetchAssignments, showSuccess, showError])

  const handleEdit = (assignment: ScheduleAssignment) => {
    setEditorState({ open: true, assignment })
  }
  const handleAddNew = (defaultDate?: string, defaultUserId?: number) => {
    setEditorState({ open: true, defaultDate, defaultUserId })
  }

  if (!clientId) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded">
        Wybierz referat w prawym górnym rogu, aby zobaczyć grafik.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📅 Grafik pracy</h1>
          <p className="text-sm text-gray-500">
            Referat: <span className="font-medium">{clientName}</span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            {(['week', 'month', 'matrix'] as ViewMode[]).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm font-medium border ${
                  view === v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                } ${v === 'week' ? 'rounded-l-md' : ''} ${v === 'matrix' ? 'rounded-r-md' : ''}`}
              >
                {v === 'week' ? 'Tydzień' : v === 'month' ? 'Miesiąc' : 'Matryca'}
              </button>
            ))}
          </div>

          <label className="inline-flex items-center text-sm text-gray-700 ml-1">
            <input
              type="checkbox"
              className="mr-1.5"
              checked={onlyMine}
              onChange={(e) => setOnlyMine(e.target.checked)}
            />
            Tylko mój grafik
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 justify-between bg-white border border-gray-200 rounded-lg p-2">
        <div className="flex items-center gap-1">
          <button onClick={goPrev} className="px-2 py-1 rounded hover:bg-gray-100" title="Poprzedni">◀</button>
          <button onClick={goToday} className="px-2 py-1 rounded text-sm hover:bg-gray-100">Dziś</button>
          <button onClick={goNext} className="px-2 py-1 rounded hover:bg-gray-100" title="Następny">▶</button>
          <span className="ml-2 font-medium text-gray-800">{rangeLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <button
                onClick={() => setTemplatesModalOpen(true)}
                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                title="Zarządzaj szablonami zmian"
              >
                🎨 Szablony
              </button>
              <button
                onClick={() => setTeamsModalOpen(true)}
                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                title="Zarządzaj zespołami pracowników"
              >
                👥 Zespoły
              </button>
              <button
                onClick={() => setBulkOpen(true)}

                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                📋 Masowe przypisanie
              </button>
              <button
                onClick={() => handleAddNew(formatDateISO(new Date()))}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                + Dodaj
              </button>
            </>
          )}
        </div>
      </div>

      {/* Legenda */}
      {templates.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-500">Legenda:</span>
          {templates.map(t => (
            <span
              key={t.id}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${
                t.category === 'oncall' ? 'border-dashed' : 'border-solid'
              }`}
              style={{ borderColor: t.color, color: '#374151' }}
              title={t.description || ''}
            >
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: t.color }} />
              {t.name}
              {t.category === 'oncall' && <span>🟠</span>}
            </span>
          ))}
        </div>
      )}

      {loading && <div className="text-sm text-gray-500">Ładowanie…</div>}

      {!loading && view === 'week' && (
        <ScheduleWeekView
          anchor={anchor}
          assignments={assignments}
          canManage={canManage}
          onEdit={handleEdit}
          onAdd={handleAddNew}
          onDelete={handleDeleteAssignment}
          currentUserId={me?.id ?? null}
        />
      )}
      {!loading && view === 'month' && (
        <ScheduleMonthView
          anchor={anchor}
          assignments={assignments}
          onAdd={canManage ? handleAddNew : undefined}
          onEdit={handleEdit}
          currentUserId={me?.id ?? null}
        />
      )}
      {!loading && view === 'matrix' && (
        <ScheduleUserMatrixView
          anchor={anchor}
          assignments={assignments}
          users={users}
          canManage={canManage}
          onAdd={handleAddNew}
          onEdit={handleEdit}
          onDelete={handleDeleteAssignment}
          currentUserId={me?.id ?? null}
        />
      )}

      {templatesModalOpen && (
        <ShiftTemplatesModal
          token={token}
          clientId={clientId}
          templates={templates}
          onClose={() => setTemplatesModalOpen(false)}
          onChanged={fetchTemplates}
        />
      )}
      {bulkOpen && (
        <BulkAssignModal
          token={token}
          clientId={clientId}
          templates={templates}
          users={users}
          teams={teams}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); fetchAssignments() }}
        />
      )}
      {teamsModalOpen && (
        <TeamsModal
          token={token}
          clientId={clientId}
          users={users}
          onClose={() => setTeamsModalOpen(false)}
          onChanged={fetchTeams}
        />
      )}

      {editorState.open && (
        <AssignmentEditorModal
          token={token}
          clientId={clientId}
          templates={templates}
          users={users}
          assignment={editorState.assignment}
          defaultDate={editorState.defaultDate}
          defaultUserId={editorState.defaultUserId}
          onClose={() => setEditorState({ open: false })}
          onSaved={() => { setEditorState({ open: false }); fetchAssignments() }}
        />
      )}
    </div>
  )
}
